import type { Credential } from './api';

export interface CredentialDisplayState {
  label: string;
  /** Tailwind classes for the badge. */
  tone: string;
  /** Title text, so the colour is never the only carrier of meaning. */
  hint: string;
}

/**
 * What a credential's badge says, and in which colour.
 *
 * The server stores two states — active and revoked — because those are the two
 * that decide whether a secret authenticates. The dashboard shows three, by
 * splitting `active` on whether the secret has ever been used:
 *
 * | Badge        | Colour | Meaning                                        |
 * |--------------|--------|------------------------------------------------|
 * | Ready to use | amber  | Issued, never presented by an agent yet        |
 * | Active       | green  | An agent has authenticated with it             |
 * | Inactive     | red    | Rotated or revoked; it no longer authenticates |
 *
 * The distinction is worth drawing because those two `active` cases need
 * opposite things from the operator: one is waiting on `waveqa setup`, the
 * other is finished. Amber says "you have a step left"; green says "nothing to
 * do". Showing both as green told someone their setup was complete when the
 * secret had never left the browser.
 *
 * Derived rather than stored. `last_used_at` already carries this fact, and a
 * status column duplicating it would be one more thing that can disagree.
 *
 * Kept in its own module, free of React, so the mapping can be exercised
 * directly — it is the part with the branches, and it should not need a browser
 * and a logged-in session to check.
 */
export function displayState(credential: Credential): CredentialDisplayState {
  if (credential.status === 'revoked') {
    return {
      label: 'Inactive',
      tone: 'text-fail-400 border-fail-600/40',
      hint:
        credential.revoked_reason === 'rotated'
          ? 'Replaced by a rotation. This secret no longer works.'
          : 'Revoked. This secret no longer works.',
    };
  }

  // Never presented by an agent. `last_used_at` is stamped on the first token
  // exchange, which is what `waveqa setup` performs when it verifies the
  // credential — so this flips to Active as soon as setup succeeds.
  if (!credential.last_used_at) {
    return {
      label: 'Ready to use',
      tone: 'text-warn-400 border-warn-600/40',
      hint: 'Issued but not yet used. Run waveqa setup on the machine that will use it.',
    };
  }

  return {
    label: 'Active',
    tone: 'text-pass-400 border-pass-600/40',
    hint: 'In use — an agent has authenticated with this credential.',
  };
}
